// Function index:
//   VenueKind          — the eight backdrop scenes.
//   BACKDROPS          — inner-SVG per venue for a 0 0 420 190 stage box
//                        (unique gradient ids: ew-sky-<venue>).
//   venueFromTerrain(terrain, roomVenue) — server terrain string → VenueKind.

export type VenueKind = 'grass' | 'forest' | 'hill' | 'mountain' | 'desert' | 'water' | 'cave' | 'room';

const GROUND = (top: string, body: string): string =>
  `<rect y="145" width="420" height="45" fill="${body}"/><rect y="145" width="420" height="4" fill="${top}"/>`;

const SKY = (venue: string, stops: string): string =>
  `<defs><linearGradient id="ew-sky-${venue}" x1="0" y1="0" x2="0" y2="1">${stops}</linearGradient></defs>` +
  `<rect width="420" height="145" fill="url(#ew-sky-${venue})"/>`;

export const BACKDROPS: Record<VenueKind, string> = {
  grass:
    SKY('grass', '<stop offset="0" stop-color="#1b2a4a"/><stop offset="0.7" stop-color="#4a3a58"/><stop offset="1" stop-color="#b06a3a"/>') +
    '<circle cx="330" cy="38" r="14" fill="#f2d8a0" opacity="0.85"/>' +
    '<polygon points="0,145 90,85 200,145" fill="#1d2438"/><polygon points="240,145 330,100 420,145" fill="#232c42"/>' +
    GROUND('#3a3020', '#2b2418'),
  forest:
    SKY('forest', '<stop offset="0" stop-color="#0f1d1a"/><stop offset="1" stop-color="#2b4034"/>') +
    '<polygon points="30,145 55,60 80,145" fill="#122019"/><polygon points="90,145 120,45 150,145" fill="#0d1813"/>' +
    '<polygon points="270,145 300,50 330,145" fill="#0d1813"/><polygon points="340,145 365,70 390,145" fill="#122019"/>' +
    GROUND('#233021', '#1a2418'),
  hill:
    SKY('hill', '<stop offset="0" stop-color="#243352"/><stop offset="1" stop-color="#7a5a48"/>') +
    '<path d="M0 145 Q105 70 210 145 Z" fill="#2a3450"/><path d="M180 145 Q315 85 420 145 Z" fill="#202a44"/>' +
    GROUND('#3d3423', '#2e281a'),
  mountain:
    SKY('mountain', '<stop offset="0" stop-color="#1a2438"/><stop offset="1" stop-color="#4e5a74"/>') +
    '<polygon points="20,145 120,30 220,145" fill="#28324c"/><polygon points="98,62 120,30 142,62 120,52" fill="#c8d2e4"/>' +
    '<polygon points="200,145 310,55 420,145" fill="#1e2740"/>' +
    GROUND('#3a3c44', '#2a2c33'),
  desert:
    SKY('desert', '<stop offset="0" stop-color="#c27b3a"/><stop offset="1" stop-color="#e8b46a"/>') +
    '<circle cx="90" cy="45" r="18" fill="#f8e0b0"/>' +
    '<path d="M0 145 Q120 118 260 140 T420 138 V145 Z" fill="#b78a4e"/>' +
    GROUND('#a3792f', '#8a6526'),
  water:
    SKY('water', '<stop offset="0" stop-color="#16283e"/><stop offset="1" stop-color="#2c4a62"/>') +
    '<rect y="110" width="420" height="35" fill="#20455c"/><path d="M0 118 Q60 114 120 118 T240 118 T360 118 T420 118" stroke="#3b6a84" stroke-width="2" fill="none"/>' +
    GROUND('#4a4632', '#37341f'),
  cave:
    SKY('cave', '<stop offset="0" stop-color="#0c0d14"/><stop offset="1" stop-color="#221d28"/>') +
    '<polygon points="40,0 70,145 10,145" fill="#161320"/><polygon points="360,0 400,145 330,145" fill="#161320"/>' +
    '<polygon points="150,0 165,60 180,0" fill="#1a1626"/><polygon points="250,0 262,80 274,0" fill="#141020"/>' +
    GROUND('#2c2433', '#1e1826'),
  room:
    SKY('room', '<stop offset="0" stop-color="#191420"/><stop offset="1" stop-color="#2e2433"/>') +
    '<rect x="60" y="30" width="18" height="115" fill="#241c2c"/><rect x="342" y="30" width="18" height="115" fill="#241c2c"/>' +
    '<circle cx="120" cy="60" r="8" fill="#e8b64c" opacity="0.35"/><circle cx="300" cy="60" r="8" fill="#e8b64c" opacity="0.35"/>' +
    GROUND('#3a3040', '#292231'),
};

const INTERIOR_KINDS = new Set(['cave', 'tower', 'labyrinth', 'dungeon', 'ruin']);
const KNOWN: ReadonlySet<string> = new Set(['grass', 'forest', 'hill', 'mountain', 'desert', 'water']);

export function venueFromTerrain(terrain: string | null | undefined, roomVenue: boolean): VenueKind {
  if (roomVenue) return 'room';
  const t = (terrain ?? '').toLowerCase();
  if (INTERIOR_KINDS.has(t)) return 'cave';
  if (KNOWN.has(t)) return t as VenueKind;
  return 'grass';
}
